import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import ChatSidebar from './ChatSidebar';
import ChatWindow from './ChatWindow';
import NewChatModal from './NewChatModal';
import { fetchConversations, fetchUnreadCount } from './ChatAPI';
import './ChatWidget.css';

const ADMIN_PATHS = [
    '/admin-panel', '/add-vehicle', '/vehicles', '/view-user', '/create-account',
    '/view-sales', '/add-new-sale', '/documents-shipping', '/volet', '/create-volet',
    '/view-payments', '/create-allocate', '/accounts', '/portfolio', '/edit-vehicle',
    '/auction', '/create-group', '/view-groups', '/view-bids', '/view-leads',
    '/view-client', '/view-stock', '/view-sale-order', '/view-invoice',
];

const POLL_UNREAD_MS  = 8000;   // unread badge poll
const POLL_THREADS_MS = 6000;   // conversation list refresh
const POLL_ACTIVE_MS  = 4000;   // active thread refresh

const ChatWidget = () => {
    const location = useLocation();
    const isAdminRoute = ADMIN_PATHS.some(p => location.pathname.startsWith(p));
    const isLoggedIn = !!localStorage.getItem('token');

    const [open, setOpen]                     = useState(false);
    const [conversations, setConversations]   = useState([]);
    const [activeThread, setActiveThread]     = useState(null);   // full thread object
    const [unread, setUnread]                 = useState(0);
    const [showNewChat, setShowNewChat]       = useState(false);
    const [mobileView, setMobileView]         = useState('list'); // 'list' | 'thread' — for small screens

    // -------- Fetch conversation list --------
    const loadConversations = useCallback(async () => {
        try {
            const res = await fetchConversations();
            if (res.success) setConversations(res.data);
        } catch (err) {
            // Silent fail — don't spam users if backend hiccups
            if (err?.response?.status !== 401) console.warn('Chat conversations error:', err);
        }
    }, []);

    // -------- Fetch unread count for the badge --------
    const loadUnread = useCallback(async () => {
        try {
            const res = await fetchUnreadCount();
            if (res.success) setUnread(res.unread || 0);
        } catch (_) {}
    }, []);

    // -------- Initial load when admin + logged in --------
    useEffect(() => {
        if (!isAdminRoute || !isLoggedIn) return;
        loadConversations();
        loadUnread();
    }, [isAdminRoute, isLoggedIn, loadConversations, loadUnread]);

    // -------- Polling loops --------
    useEffect(() => {
        if (!isAdminRoute || !isLoggedIn) return;

        const unreadTimer = setInterval(loadUnread, POLL_UNREAD_MS);
        let threadsTimer;
        if (open) threadsTimer = setInterval(loadConversations, POLL_THREADS_MS);

        return () => {
            clearInterval(unreadTimer);
            if (threadsTimer) clearInterval(threadsTimer);
        };
    }, [isAdminRoute, isLoggedIn, open, loadUnread, loadConversations]);

    // -------- Don't render at all on public routes --------
    if (!isAdminRoute || !isLoggedIn) return null;

    const handleSelectThread = (thread) => {
        setActiveThread(thread);
        setMobileView('thread');
    };

    const handleBackToList = () => {
        setMobileView('list');
        setActiveThread(null);
    };

    const handleThreadCreated = (threadId, threadObj) => {
        // Called after we successfully send a first message
        setShowNewChat(false);
        loadConversations();
        if (threadObj) {
            setActiveThread(threadObj);
            setMobileView('thread');
        }
    };

    const handleOpen = () => {
        setOpen(true);
        loadUnread();
        loadConversations();
    };

    return (
        <>
            {/* ============ LAUNCHER BUTTON ============ */}
            <button
                className={`skc-chat-launcher ${open ? 'is-open' : ''}`}
                onClick={() => (open ? setOpen(false) : handleOpen())}
                aria-label={open ? 'Close chat' : 'Open chat'}
            >
                {open ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                ) : (
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                    </svg>
                )}
                {!open && unread > 0 && (
                    <span className="skc-chat-badge">{unread > 99 ? '99+' : unread}</span>
                )}
            </button>

            {/* ============ PANEL ============ */}
            <div className={`skc-chat-panel ${open ? 'is-open' : ''} view-${mobileView}`}>
                <div className="skc-chat-header">
                    <div className="skc-chat-header-title">
                        <span className="skc-chat-header-dot" />
                        <span>Messages</span>
                        {unread > 0 && <span className="skc-chat-header-unread">{unread} new</span>}
                    </div>
                    <button className="skc-chat-close" onClick={() => setOpen(false)} aria-label="Close">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                </div>

                <div className="skc-chat-body">
                    <ChatSidebar
                        conversations={conversations}
                        activeThreadId={activeThread?.thread_id}
                        onSelect={handleSelectThread}
                        onNewChat={() => setShowNewChat(true)}
                        myId={parseInt(localStorage.getItem('id') || localStorage.getItem('user_id') || '0', 10)}
                    />
                    <ChatWindow
                        thread={activeThread}
                        onBack={handleBackToList}
                        onRefreshList={loadConversations}
                        onRefreshUnread={loadUnread}
                    />
                </div>
            </div>

            {/* ============ NEW CHAT MODAL ============ */}
            {showNewChat && (
                <NewChatModal
                    onClose={() => setShowNewChat(false)}
                    onCreated={handleThreadCreated}
                />
            )}
        </>
    );
};

export default ChatWidget;